import { ReactNode, useEffect, useMemo } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { portalSettingsGet } from "store/slices/dashboardSlice";
import { RootState } from "store/store";
import { isAllowedClusterId } from "utils/clusterAccess";
import { decode } from "utils/HelperFunctions";
import { decodeLegacyUrlValue } from "utils/legacyUrlDecoder";
import { getCurrentSlug } from "utils/currentSlug";
import { getCustomDomainRedirect } from "utils/customDomainRouting";
import { getExternalClusterRedirect } from "config/externalClusterRedirects";
import { isPortalAvailable } from "utils/portalAvailability";
import PortalMaintenanceRoute from "components/auth/PortalMaintenanceRoute";
import { invalidRouteState, rememberClusterId } from "utils/invalidRoute";

const CLUSTER_ID_PATTERN = /^[1-9]\d{0,19}$/;

const getValidatedClusterId = (value: unknown): string | null =>
  typeof value === "string" && CLUSTER_ID_PATTERN.test(value) ? value : null;

const decodeClusterSlug = (clusterSlug: string): string | null => {
  try {
    return getValidatedClusterId(decode(clusterSlug, true));
  } catch {
    return null;
  }
};

/**
 * Resolves the tenant represented by every supported URL shape: canonical
 * cluster routes, public action slugs, and the legacy four-part payment link.
 */
export const getPortalClusterIdFromPath = (pathname: string): string | null => {
  const segments = pathname.split("/").filter(Boolean);
  const firstSegment = segments[0];

  if (!firstSegment || firstSegment === "invalid") {
    return null;
  }

  try {
    if (firstSegment.startsWith("ci_")) {
      return getValidatedClusterId(decode(firstSegment, true));
    }

    const currentSlug = getCurrentSlug(pathname);
    const clusterIdFromSlug = decode(currentSlug || "")?.split("_")?.[1];
    const validatedSlugClusterId = getValidatedClusterId(clusterIdFromSlug);

    if (validatedSlugClusterId) {
      return validatedSlugClusterId;
    }

    // Legacy payment links encode the cluster as the first of four segments.
    if (segments.length === 4) {
      return getValidatedClusterId(decodeLegacyUrlValue(firstSegment));
    }
  } catch {
    // Malformed URL values are handled by the existing invalid-route logic.
  }

  return null;
};

const PortalAvailabilityRoute = ({ children }: { children: ReactNode }) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const hostname =
    typeof window === "undefined" ? "" : window.location.hostname;
  const customDomainRedirect = useMemo(
    () =>
      getCustomDomainRedirect({
        hostname,
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
        decodeClusterSlug,
      }),
    [hostname, location.hash, location.pathname, location.search]
  );
  const externalRedirect = useMemo(
    () => getExternalClusterRedirect(location.pathname, decodeClusterSlug),
    [location.pathname]
  );
  const clusterId = useMemo(
    () => getPortalClusterIdFromPath(location.pathname),
    [location.pathname]
  );
  const { portalSettings, portalSettingsStatus, portalSettingsClusterId } =
    useAppSelector((state: RootState) => state.dashboard);
  const hasAllowedCluster = isAllowedClusterId(clusterId);
  const hasCurrentSettings =
    Boolean(clusterId) && portalSettingsClusterId === clusterId;

  useEffect(() => {
    if (clusterId && hasAllowedCluster) {
      rememberClusterId(clusterId);
    }
  }, [clusterId, hasAllowedCluster]);

  useEffect(() => {
    if (externalRedirect) {
      window.location.replace(externalRedirect);
    }
  }, [externalRedirect]);

  useEffect(() => {
    if (
      externalRedirect ||
      customDomainRedirect ||
      !clusterId ||
      !hasAllowedCluster
    ) {
      return;
    }

    const hasAttemptedCurrentSettings =
      portalSettingsStatus !== "idle" && hasCurrentSettings;

    if (!hasAttemptedCurrentSettings) {
      dispatch(portalSettingsGet({ cluster_id: clusterId }));
    }
  }, [
    clusterId,
    customDomainRedirect,
    dispatch,
    externalRedirect,
    hasAllowedCluster,
    hasCurrentSettings,
    portalSettingsStatus,
  ]);

  if (externalRedirect) {
    return null;
  }

  if (customDomainRedirect) {
    return (
      <Navigate
        to={customDomainRedirect}
        replace={true}
        state={
          customDomainRedirect.pathname === "/invalid"
            ? invalidRouteState("not-found")
            : undefined
        }
      />
    );
  }

  if (location.pathname !== "/invalid" && clusterId && !hasAllowedCluster) {
    return (
      <Navigate
        to="/invalid"
        replace={true}
        state={invalidRouteState("not-found")}
      />
    );
  }

  if (
    location.pathname !== "/invalid" &&
    portalSettingsStatus === "succeeded" &&
    hasCurrentSettings &&
    !isPortalAvailable(portalSettings)
  ) {
    return (
      <Navigate
        to="/invalid"
        replace={true}
        state={invalidRouteState("unavailable")}
      />
    );
  }

  return <PortalMaintenanceRoute>{children}</PortalMaintenanceRoute>;
};

export default PortalAvailabilityRoute;
